import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ujmsGlobalSearch, ujmsAskAi } from "../../services/ujmsApi";
import { Panel, Badge, thCls, tdCls, inputCls, btnPrimary, btnGhost, cls, fmtDT } from "../ui";
import { ScanSearch, Sparkles, Loader2, FileText, Users, Gavel, Inbox, FolderOpen } from "lucide-react";

const TYPES = [
  { key: "cases", label: "Cases", icon: FolderOpen },
  { key: "persons", label: "Persons", icon: Users },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "filings", label: "Filings", icon: Inbox },
  { key: "orders", label: "Orders", icon: Gavel },
];

const typesFromUrl = (value) => value === "none" ? [] : value ? TYPES.map((t) => t.key).filter((key) => value.split(",").includes(key)) : TYPES.map((t) => t.key);

export default function GlobalSearch({ centralized = false, scope }) {
  const [params, setParams] = useSearchParams();
  const urlQuery = params.get("q") || "";
  const urlTypes = centralized ? (scope === "all" ? null : scope === "people" ? "persons" : scope) : params.get("type");
  const [q, setQ] = useState(params.get("q") || "");
  const [active, setActive] = useState(() => typesFromUrl(urlTypes));
  const requestId = useRef(0);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [ai, setAi] = useState(null);

  const search = useCallback(async (query, types) => {
    const id = ++requestId.current;
    if ((query || "").trim().length < 2 || !types.length) { setResults(null); setBusy(false); return; }
    setBusy(true);
    try {
      const r = await ujmsGlobalSearch(query.trim(), types.join(","));
      if (id === requestId.current) setResults(r.data);
    } catch (e) { if (id === requestId.current) { setResults(null); toast.error(e.response?.data?.detail || "Search failed"); } }
    finally { if (id === requestId.current) setBusy(false); }
  }, []);

  useEffect(() => {
    const types = typesFromUrl(urlTypes);
    setQ(urlQuery); setActive(types); setAi(null); setResults(null);
    search(urlQuery, types);
  }, [urlQuery, urlTypes, search]);

  const submitSearch = (query, types) => {
    const next = new URLSearchParams();
    if (query.trim()) next.set("q", query.trim());
    if (types.length !== TYPES.length) next.set("type", types.join(",") || "none");
    if (next.toString() === params.toString()) search(query, types);
    else setParams(next);
  };

  const toggleType = (key) => {
    const next = active.includes(key) ? active.filter((k) => k !== key) : [...active, key];
    submitSearch(q, next);
  };

  const ask = async () => {
    if (q.trim().length < 3) { toast.error("Ask a fuller question"); return; }
    setAiBusy(true); setAi(null);
    try {
      const r = await ujmsAskAi(q.trim());
      setAi(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "AI answer failed"); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="global-search-page">
      {!centralized && <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"><ScanSearch className="w-6 h-6" /> Global Search</h1>
        <p className="text-sm text-slate-500">One query across cases, persons, documents, filings and orders — confidentiality is enforced server-side. Ask the AI for a synthesized answer with case citations.</p>
      </div>}

      {!centralized && <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); submitSearch(q, active); }} data-testid="gs-form">
        <input className={`${inputCls} flex-1 max-w-2xl`} value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="26CR700010, Thornton, bench warrant, sentencing order…" data-testid="gs-input" />
        <button type="submit" className={btnPrimary} disabled={busy || !active.length || q.trim().length < 2} data-testid="gs-search-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
        </button>
        <button type="button" className="inline-flex items-center gap-1.5 border border-amber-400 bg-amber-50 text-amber-800 text-sm font-medium px-3 py-1.5 rounded-sm hover:bg-amber-100 transition-colors"
          disabled={aiBusy} onClick={ask} data-testid="gs-ask-ai-btn">
          {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Ask AI
        </button>
      </form>}

      {!centralized && <div className="flex gap-1.5 flex-wrap">
        {TYPES.map((t) => (
          <button key={t.key} onClick={() => toggleType(t.key)} aria-pressed={active.includes(t.key)} data-testid={`gs-filter-${t.key}`}
            className={cls("inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors",
              active.includes(t.key) ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-500 border-slate-300 hover:bg-slate-100")}>
            <t.icon className="w-3 h-3" /> {t.label}
          </button>
        ))}
      </div>}
      {centralized && q.trim().length < 2 && <p className="text-sm text-slate-500" data-testid="central-search-empty-query">Enter at least two characters in the search bar above.</p>}

      {ai && (
        <Panel title="AI answer — synthesized from matching court records" testId="gs-ai-panel">
          <div className="p-4">
            <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="gs-ai-answer">{ai.answer}</p>
            {ai.sources?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {ai.sources.map((s) => (
                  <Link key={s.id} to={`/ujms/cases/${s.id}`} className="font-mono text-xs border border-blue-200 bg-blue-50 text-blue-900 rounded-sm px-1.5 py-0.5 hover:bg-blue-100"
                    data-testid={`gs-ai-source-${s.case_number}`}>
                    {s.case_number}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </Panel>
      )}

      {results && (
        <div className="space-y-4" data-testid="gs-results">
          <p className="text-xs text-slate-500" data-testid="gs-results-summary">{results.total} result(s) for “{results.query}”</p>
          {results.results.cases?.length > 0 && (
            <Panel title={`Cases (${results.results.cases.length})`} testId="gs-cases-panel">
              <table className="w-full border-collapse">
                <thead><tr><th className={thCls}>Case Number</th><th className={thCls}>Caption</th><th className={thCls}>Type</th><th className={thCls}>Status</th></tr></thead>
                <tbody>
                  {results.results.cases.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className={tdCls}><Link className="font-mono text-blue-900 hover:underline" to={`/ujms/cases/${c.id}`} data-testid={`gs-case-${c.case_number}`}>{c.case_number}</Link></td>
                      <td className={tdCls}>{c.caption}</td>
                      <td className={tdCls}><Badge value={c.case_type} /></td>
                      <td className={tdCls}><Badge value={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}
          {results.results.persons?.length > 0 && (
            <Panel title={`Persons (${results.results.persons.length})`} testId="gs-persons-panel">
              <table className="w-full border-collapse">
                <thead><tr><th className={thCls}>Name</th><th className={thCls}>DOB</th></tr></thead>
                <tbody>
                  {results.results.persons.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className={tdCls}>{p.display_name}</td>
                      <td className={`${tdCls} font-mono text-xs`}>{p.dob || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}
          {["documents", "filings", "orders"].map((k) => (results.results[k]?.length > 0 && (
            <Panel key={k} title={`${k[0].toUpperCase() + k.slice(1)} (${results.results[k].length})`} testId={`gs-${k}-panel`}>
              <table className="w-full border-collapse">
                <thead><tr><th className={thCls}>Title</th><th className={thCls}>Case</th><th className={thCls}>Detail</th><th className={thCls}>Created</th></tr></thead>
                <tbody>
                  {results.results[k].map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className={tdCls}>{r.title}
                        {r.match === "full-text" && <span className="ml-1.5 text-[9px] font-bold uppercase bg-amber-100 text-amber-700 border border-amber-300 rounded-sm px-1" data-testid={`gs-fulltext-${r.id}`}>text match</span>}
                        {r.snippet && <div className="text-[11px] text-slate-400 italic truncate max-w-md">{r.snippet}</div>}</td>
                      <td className={tdCls}><Link className="font-mono text-blue-900 hover:underline" to={`/ujms/cases/${r.case_id}`}>{r.case_number}</Link></td>
                      <td className={`${tdCls} text-xs`}>{r.document_type || r.filing_type || r.order_type || "—"} {r.status ? `· ${r.status}` : ""}</td>
                      <td className={`${tdCls} font-mono text-xs`}>{fmtDT(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )))}
          {results.total === 0 && <p className="text-sm text-slate-400" data-testid="gs-empty">No records matched. Try fewer or different terms.</p>}
        </div>
      )}
    </div>
  );
}
